import type { Locale } from "@commerce-frontend/i18n/types";
import { storyblokDataAttributes } from "@commerce-frontend/storyblok-preview/component";
import { graphql } from "@commerce-frontend/types";
import { Container } from "@commerce-frontend/ui/base/Container/Container";
import { Section as BaseSection } from "@commerce-frontend/ui/base/Section/Section";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { isValue } from "@labdigital/toolkit";
import type { HTMLAttributes } from "react";
import { RenderBlock } from "../RenderBlock/RenderBlock";

export const SectionFragment = graphql(/* GraphQL */ `
	fragment SectionFragment on Section {
		__typename
		id
		backgroundColor
		size
		spacing
		body {
			...TeaserRowFragment
			...RichTextFragment
			...TwoColumnFragment
			...ThreeColumnFragment
			...FourColumnFragment
			...SectionHeaderFragment
			...ImageFragment
			...DividerFragment
			...IframeFragment
			...ButtonsFragment
			...BlockQuoteFragment
			...TableFragment
			...NewsletterCTAFragment
			...UGCFlowboxFragment
			...CookieDeclarationFragment
			...VideoFragment
			...FAQItemListFragment
			...ShopSectionFragment
			...CategorySectionFragment
			...JafixFragment
		}
	}
`);

type Props = {
	data: ResultOf<typeof SectionFragment>;
	locale: Locale;
} & HTMLAttributes<HTMLDivElement>;

export const Section = ({ data, locale, ...attributes }: Props) => {
	const { backgroundColor, spacing, size, body } = data;

	return (
		<BaseSection
			backgroundColor={backgroundColor ?? undefined}
			spacing={spacing ?? undefined}
			className={cn(attributes.className)}
			{...attributes}
			{...storyblokDataAttributes(data)}
		>
			<Container size={size ?? undefined} gap="large">
				{body?.filter(isValue).map((block) => {
					if (!("__typename" in block)) {
						return null;
					}

					return <RenderBlock key={block.id} block={block} locale={locale} wrapContent={false} />;
				})}
			</Container>
		</BaseSection>
	);
};
